import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlaceIcon from '@mui/icons-material/Place';
import PersonIcon from '@mui/icons-material/Person';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { MarkdownView } from '../components/MarkdownView';
import { useDateTimeFormat } from '../preferences/DateTimeFormatContext';
import { downloadIcs, googleCalendarUrl } from '../util/calendarExport';
import { carUrl } from '../lib/slugId';
import type { Car, Event as EventModel, Paginated, SingleResponse } from '../api/types';
import { CarCardMedia } from '../components/CarCardMedia';

export function EventDetail() {
  const { slugId } = useParams<{ slugId: string }>();
  const { t } = useTranslation();
  const { formatDateTime } = useDateTimeFormat();
  const [event, setEvent] = useState<EventModel | null>(null);
  const [organizerCars, setOrganizerCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!slugId) return;
    apiFetch<SingleResponse<EventModel>>(`/api/v1/events/${slugId}?include=organizer`)
      .then((res) => setEvent(res.data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slugId]);

  // A garage sale is only as readable as the seller's garage — the cars they own
  // hint at the parts on offer, so surface them here.
  useEffect(() => {
    const organizerId = event?.organizer?.id;
    if (!organizerId || event?.type !== 'garage_sale') return;
    apiFetch<Paginated<Car>>(
      `/api/v1/cars?filter[user_id]=${organizerId}&filter[is_public]=1&per_page=12`,
    )
      .then((res) => setOrganizerCars(res.data))
      .catch(() => setOrganizerCars([]));
  }, [event?.organizer?.id, event?.type]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !event) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
        <Alert severity="error">{error || t('eventDetail.notFound')}</Alert>
      </Container>
    );
  }

  const locationLine = [event.location_name, event.city, event.country].filter(Boolean).join(', ');

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
      <PageHeader
        crumbs={[
          { label: t('nav.events'), to: '/events' },
          { label: event.title },
        ]}
      />

      <Stack spacing={4}>
        {/* Title + type */}
        <Stack direction="row" spacing={2} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Typography variant="h3">{event.title}</Typography>
          <Chip label={event.type_label} />
        </Stack>

        {/* Photos */}
        {event.photo_urls.length > 0 && (
          <Grid container spacing={2}>
            {event.photo_urls.map((url, idx) => (
              <Grid key={idx} size={{ xs: 12, sm: event.photo_urls.length > 1 ? 6 : 12 }}>
                <Box
                  component="img"
                  src={url}
                  alt={`${event.title} ${idx + 1}`}
                  sx={{
                    width: '100%',
                    maxHeight: 420,
                    objectFit: 'cover',
                    borderRadius: 1,
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Details */}
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ScheduleIcon color="action" />
            <Typography variant="body1">
              {formatDateTime(event.starts_at)}
              {event.ends_at && ` — ${formatDateTime(event.ends_at)}`}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <PlaceIcon color="action" />
            <Typography variant="body1">{locationLine}</Typography>
          </Stack>

          {event.organizer && (
            <Stack direction="row" spacing={1} alignItems="center">
              <PersonIcon color="action" />
              <Link
                component={RouterLink}
                to={`/users/${event.organizer.id}`}
                variant="body1"
                underline="hover"
              >
                {event.organizer.name}
              </Link>
            </Stack>
          )}
        </Stack>

        {organizerCars.length > 0 && (
          <Stack spacing={2}>
            <Typography variant="h6">{t('eventDetail.organizerCars')}</Typography>
            <Stack direction="row" spacing={2} sx={{ overflowX: 'auto', pb: 1 }}>
              {organizerCars.map((car) => (
                <Card key={car.id} sx={{ minWidth: 220, flexShrink: 0 }}>
                  <CardActionArea component={RouterLink} to={carUrl(car)}>
                    <CarCardMedia car={car} height={120} />
                    <CardContent>
                      <Typography variant="subtitle2" noWrap>
                        {car.display_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {car.year} {car.make} {car.model}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Stack>
          </Stack>
        )}

        {event.description && (
          <>
            <Divider />
            <MarkdownView>{event.description}</MarkdownView>
          </>
        )}

        {/* Calendar actions */}
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => downloadIcs(event)}
          >
            {t('events.downloadIcs')}
          </Button>
          <Button
            variant="contained"
            startIcon={<OpenInNewIcon />}
            href={googleCalendarUrl(event)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('events.addToGoogleCalendar')}
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
}
